import React, { useState, useEffect } from 'react';

import { Modal, Table, Select, Splitter, notification, Divider } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    QuestionCircleOutlined,
    LoadingOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    CheckOutlined,
    MenuOutlined,
    OrderedListOutlined,
} from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';
import WaypointHeader from '@components/waypoints/waypoint-header';
import Waypoint from '@components/waypoints/waypoint';

import Toolbar, { ICommandProps } from '@controls/toolbar/toolbar';

import { userLoaded } from '@store/actions';

import { exception, securityRestriction } from '@extensions/notification';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getEnumList } from '@extensions/utils';

import { IUserSession } from '@entities/user-session';
import { IWaypoint } from '@entities/waypoint';
import { IWaypointFilter } from '@entities/waypoint-filter';
import { IWaypointHeader } from '@entities/waypoint-header';

import { Permission, hasPermission } from '@enums/permission';
import { enumLabel as waypointTypeLabel } from '@enums/waypoint-type';
import { RouteZone, enumLabel as routeZoneLabel } from '@enums/route-zone';

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}

const Waypoints = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [headers, setHeaders] = useState<Array<IWaypointHeader>>([]);
    const [currentHeader, setCurrentHeader] = useState<IWaypointHeader | undefined>(undefined);
    const [selectedHeaderIds, setSelectedHeaderIds] = useState<React.Key[]>();
    const [headerRefreshRequired, setHeaderRefreshRequired] = useState<boolean>(true);
    const [headersLoading, setHeaderLoading] = useState<boolean>(false);
    const [openHeader, setOpenHeader] = useState<boolean>(false);

    const [waypoints, setWaypoints] = useState<Array<IWaypoint>>([]);
    const [currentWaypoint, setCurrentWaypoint] = useState<IWaypoint>();
    const [selectedWaypointIds, setSelectedWaypointIds] = useState<React.Key[]>([]);
    const [waypointRefreshRequired, setWaypointsRefreshRequired] = useState<boolean>(false);
    const [openWaypoint, setOpenWaypoint] = useState<boolean>(false);

    const [waypointLoading, setWaypointLoading] = useState<boolean>(false);
    const [routeZones] = useState(getEnumList(RouteZone, routeZoneLabel));
    const [currentRouteZone, setCurrentRouteZone] = useState<RouteZone>(RouteZone.Foreign);

    useEffect(() => {
        if (!headerRefreshRequired) return;

        setHeaderRefreshRequired(false);

        setHeaderLoading(true);

        serverFetch(`waypointheaders`, { method: 'GET' })
            .then((data: Array<IWaypointHeader>) => {
                setHeaders(data);

                let entity = data[0];
                if (entity?.id) {
                    setSelectedHeaderIds([entity.id]);
                    setCurrentHeader(entity);
                    setWaypointsRefreshRequired(true);
                }

                setHeaderLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения маршрутов', ex, () => d(userLoaded(undefined)));
                setHeaderLoading(false);
            });
    }, [headerRefreshRequired]);

    useEffect(() => {
        if (!currentHeader || !waypointRefreshRequired) return;

        setWaypointsRefreshRequired(false);
        setWaypointLoading(true);

        var filter: IWaypointFilter = { routeZone: currentRouteZone, headerId: currentHeader.id };

        serverFetch(`waypoints/all`, { method: 'GET', queryParams: filter })
            .then((data: Array<IWaypoint>) => {
                setWaypoints(data);
                setWaypointLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения точек маршрута', ex, () => d(userLoaded(undefined)));
                setWaypointLoading(false);
            });
    }, [waypointRefreshRequired, currentHeader]);

    const onDeleteHeader = () => {
        if (!currentHeader) return;

        serverFetch(`waypointheaders/${currentHeader?.id}`, { method: 'DELETE' })
            .then(() => {
                setSelectedHeaderIds([]);
                setHeaderRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления маршрута', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDeleteWaypoint = () => {
        if (!currentWaypoint) return;

        serverFetch(`waypoints/${currentWaypoint.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChangeWaypoint([]);
                setWaypointsRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления точки маршрута', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderHeaderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: headersLoading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setHeaderRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить',
                        key: 'add',
                        type: 'primary',
                        disabled: headersLoading,
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWaypoints)) {
                                securityRestriction(api, [Permission.ManageWaypoints]);
                                return;
                            }

                            setSelectedHeaderIds([]);
                            setCurrentHeader(undefined);
                            setOpenHeader(true);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentHeader,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWaypoints)) {
                                securityRestriction(api, [Permission.ManageWaypoints]);
                                return;
                            }

                            setOpenHeader(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentHeader,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteWaypoint)) {
                                securityRestriction(api, [Permission.DeleteWaypoint]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить маршрут "${currentHeader?.name}"?`,
                                okType: 'primary',
                                icon: <QuestionCircleOutlined />,
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDeleteHeader();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    const renderWaypointToolbar = () => {
        let commands: Array<ICommandProps> = [
            {
                label: 'Обновить',
                key: 'refresh',
                disabled: headersLoading,
                icon: <ReloadOutlined />,
                onClick: () => {
                    setWaypointsRefreshRequired(true);
                },
            },
            {
                label: 'Добавить',
                key: 'add',
                type: 'primary',
                icon: <PlusOutlined />,
                onClick: () => {
                    if (!hasPermission(userSession.permissions, Permission.ManageWaypoints)) {
                        securityRestriction(api, [Permission.ManageWaypoints]);
                        return;
                    }

                    setSelectedWaypointIds([]);
                    setCurrentWaypoint(undefined);
                    setOpenWaypoint(true);
                },
            },
            {
                label: 'Изменить',
                key: 'edit',
                disabled: !currentWaypoint,
                icon: <EditOutlined />,
                onClick: () => {
                    if (!hasPermission(userSession.permissions, Permission.ManageWaypoints)) {
                        securityRestriction(api, [Permission.ManageWaypoints]);
                        return;
                    }

                    setOpenWaypoint(true);
                },
            },
            {
                label: 'Удалить',
                key: 'delete',
                disabled: !currentWaypoint,
                icon: <DeleteOutlined />,
                onClick: () => {
                    if (!hasPermission(userSession.permissions, Permission.DeleteWaypoint)) {
                        securityRestriction(api, [Permission.DeleteWaypoint]);
                        return;
                    }

                    modal.confirm({
                        title: `Удалить маршрутную точку "${currentWaypoint?.name}"?`,
                        okType: 'primary',
                        icon: <QuestionCircleOutlined />,
                        okText: 'Удалить',
                        cancelText: 'Отмена',
                        onOk: () => {
                            onDeleteWaypoint();
                        },
                    });
                },
            },
            { isDivider: true },
            {
                label: 'Упорядочить',
                key: 'reorder',
                disabled: !currentHeader,
                icon: <OrderedListOutlined />,
                onClick: () => {
                    if (!hasPermission(userSession.permissions, Permission.ManageWaypoints)) {
                        securityRestriction(api, [Permission.ManageWaypoints]);
                        return;
                    }

                    modal.confirm({
                        title: `Упорядочить точки маршрута для  "${currentHeader?.name}"?`,
                        okType: 'primary',
                        icon: <QuestionCircleOutlined />,
                        okText: 'OK',
                        cancelText: 'Отмена',
                        onOk: () => {
                            serverFetch(`waypoints/order`, { method: 'POST', bodyData: waypoints })
                                .then(() => {
                                    setWaypointsRefreshRequired(true);
                                })
                                .catch((ex: any) => {
                                    exception(api, 'Ошибка сохранения маршрутных точек', ex, () => d(userLoaded(undefined)));
                                });
                        },
                    });
                },
            },
        ];

        return <Toolbar commands={commands} />;
    };

    const renderFilter = () => {
        return (
            <Select
                key='routeZones'
                placeholder='Тип'
                value={currentRouteZone}
                size='middle'
                style={{ width: 220, marginBottom: 20 }}
                onChange={(value) => setCurrentRouteZone(value)}
                options={routeZones.map((t) => {
                    return { value: t.value, label: t.label };
                })}
                disabled
            />
        );
    };

    const onSelectChangeHeader = (selectedRowKeys: React.Key[]) => {
        setSelectedHeaderIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            let entity = headers.find((o) => o.id === selectedRowKeys[0]);
            setCurrentHeader(entity);
            setWaypointsRefreshRequired(true);
        } else {
            setCurrentHeader(undefined);
        }
    };

    const onSelectChangeWaypoint = (selectedRowKeys: React.Key[]) => {
        setSelectedWaypointIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            let entity = waypoints.find((w) => w.sequence === selectedRowKeys[0]);

            setCurrentWaypoint(entity);
        } else {
            setCurrentWaypoint(undefined);
        }
    };

    const renderHeaderTable = () => {
        const columns: ColumnsType<IWaypointHeader> = [
            {
                title: 'Название',
                dataIndex: 'name',
            },
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedHeaderIds,
                    onChange: onSelectChangeHeader,
                    type: 'radio',
                    columnWidth: 32,
                }}
                columns={columns}
                dataSource={headers}
                loading={{
                    spinning: headersLoading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChangeHeader([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - 235px)` }}
            />
        );
    };

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            const activeIndex = waypoints.findIndex((i) => i.sequence === active.id);
            const overIndex = waypoints.findIndex((i) => i.sequence === over?.id);

            const result = arrayMove(waypoints, activeIndex, overIndex);

            if (active.id !== over?.id) {
                serverFetch(`waypoints/order`, { method: 'POST', bodyData: result })
                    .then(() => {
                        setWaypointsRefreshRequired(true);
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка сохранения маршрутных точек', ex, () => d(userLoaded(undefined)));
                    });
            }
        }
    };

    const Row = ({ children, ...props }: RowProps) => {
        const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
            id: props['data-row-key'],
        });

        const style: React.CSSProperties = {
            ...props.style,
            transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 })?.replace(/translate3d\(([^,]+),/, 'translate3d(0,'),
            transition,
            ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
        };

        return (
            <tr {...props} ref={setNodeRef} style={style} {...attributes}>
                {React.Children.map(children, (child) => {
                    if ((child as React.ReactElement).key === 'sort') {
                        return React.cloneElement(child as React.ReactElement, {
                            children: (
                                <MenuOutlined ref={setActivatorNodeRef} style={{ touchAction: 'none', cursor: 'move' }} {...listeners} />
                            ),
                        });
                    }
                    return child;
                })}
            </tr>
        );
    };

    const renderWaypointTable = () => {
        const columns: ColumnsType<IWaypoint> = [
            {
                key: 'sort',
                width: 40,
            },
            {
                title: '#',
                dataIndex: 'sequence',
                align: 'center',
                width: 40,
            },
            {
                title: 'Тип',
                width: 170,
                render: (_, record) => {
                    return <>{waypointTypeLabel(record.type)} </>;
                },
            },
            {
                title: 'Название',
                dataIndex: 'name',
                width: 400,
            },
            {
                title: 'Приемка груза',
                width: 120,
                render: (_, record) => {
                    return <div style={{ textAlign: 'center' }}> {record.allowAcceptance ? <CheckOutlined /> : ''}</div>;
                },
            },
            {
                title: 'Опциональная',
                width: 120,
                render: (_, record) => {
                    return <div style={{ textAlign: 'center' }}> {record.isOptional ? <CheckOutlined /> : ''}</div>;
                },
            },
            {},
        ];

        return (
            <DndContext onDragEnd={onDragEnd}>
                <SortableContext items={waypoints.map((i) => i.sequence || 1)} strategy={verticalListSortingStrategy}>
                    <Table
                        components={{
                            body: {
                                row: Row,
                            },
                        }}
                        rowKey='sequence'
                        size='small'
                        rowSelection={{
                            selectedRowKeys: selectedWaypointIds,
                            onChange: onSelectChangeWaypoint,
                            type: 'radio',
                            columnWidth: 32,
                        }}
                        columns={columns}
                        dataSource={waypoints}
                        loading={{
                            spinning: waypointLoading,
                            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                        }}
                        onRow={(record) => {
                            return {
                                onClick: (event) => {
                                    onSelectChangeWaypoint([record.id || '']);
                                },
                            };
                        }}
                        pagination={false}
                        scroll={{ y: `calc(100vh - 235px)` }}
                    />
                </SortableContext>
            </DndContext>
        );
    };

    return (
        <>
            <Splitter lazy style={{ height: '100%', paddingBottom: 20 }}>
                <Splitter.Panel defaultSize='20%' min='10%' max='40%' style={{ paddingRight: 20 }}>
                    <Divider titlePlacement='left'>Маршрутны</Divider>
                    {renderFilter()}
                    {renderHeaderToolbar()}
                    {renderHeaderTable()}
                </Splitter.Panel>
                <Splitter.Panel style={{ paddingLeft: 20 }}>
                    <Divider titlePlacement='left'>Маршрутные точки</Divider>
                    {renderWaypointToolbar()}
                    {renderWaypointTable()}
                </Splitter.Panel>
            </Splitter>

            {openHeader && (
                <WaypointHeader
                    id={currentHeader?.id}
                    api={api}
                    onCancel={() => setOpenHeader(false)}
                    serverFetch={serverFetch}
                    onSave={() => {
                        setHeaderRefreshRequired(true);
                        setOpenHeader(false);
                    }}
                />
            )}

            {openWaypoint && (
                <Waypoint
                    routeZone={currentRouteZone}
                    headerId={currentHeader?.id}
                    id={currentWaypoint?.id}
                    api={api}
                    onCancel={() => setOpenWaypoint(false)}
                    serverFetch={serverFetch}
                    onSave={() => {
                        setWaypointsRefreshRequired(true);
                        setOpenWaypoint(false);
                    }}
                />
            )}

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Waypoints;
